import './DocumentDraftList.scss';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { MdDeleteOutline, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';
import { draftDisplayTitle, type IDocumentDraft } from '@models/documentDraft/IDocumentDraft';
import { DocumentDraftService, type DocumentDraftLookup } from '@services/DocumentDraftService';
import Skeleton from '@srm/ui/skeleton';
import { plural } from '@srm/ui/lib/pluralize';
import { formatDraftWhen } from './formatDraftWhen';
import { summarizeDraftData, type IDraftPreview } from './summarizeDraftData';
import {
    draftOriginKey,
    draftOriginLabel,
    draftOriginRef,
    loadDraftOriginTitle,
    type IDraftOrigin,
} from './draftOriginTitle';

export interface IDocumentDraftListProps {
    drafts: IDocumentDraft[];
    /** Тип документа для заголовка списка: «Черновики «Заявка на потребность»». */
    documentTitle?: string;
    onOpenDraft: (draft: IDocumentDraft) => void;
    /**
     * Удалить черновик на сервере и убрать его из `drafts` — строка уходит сама. Reject — строка
     * остаётся с «Не удалось удалить · Повторить». Без обработчика корзинки в строках нет.
     */
    onDeleteDraft?: (draft: IDocumentDraft) => Promise<void>;
    /** Черновик, открытый сейчас в карточке: удалять его отсюда нельзя — только из меню капсулы. */
    activeDraftId?: string;
    emptyText?: string;
    /** Загрузка содержимого для предпросмотра; по умолчанию — GET /DocumentDraft/{id}. */
    loadDraft?: (id: string) => Promise<DocumentDraftLookup>;
    /** Имя шаблона/базового документа для пометки; по умолчанию — GET /document/scheme/{id}. */
    loadOriginTitle?: (origin: IDraftOrigin) => Promise<string | undefined>;
    /** Черновик, чей предпросмотр раскрыт сразу (витрина, VR). */
    initialPreviewId?: string;
    /** Черновик, у которого сразу показан вопрос об удалении (витрина, VR). */
    initialConfirmId?: string;
}

type PreviewState = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; preview: IDraftPreview };
type DeleteState = { id: string; kind: 'busy' | 'error' } | null;

const EASE_OUT = [0.16, 1, 0.3, 1] as const;

const defaultLoadDraft = (id: string) => DocumentDraftService.get(id);

/**
 * Список черновиков одного действия — содержимое поповеров «Все черновики» в полосе возобновления,
 * в меню капсулы и на карточке лаунчера. Строка открывает свой черновик на месте; глаз раскрывает
 * под строкой предпросмотр заполненных реквизитов — содержимое грузится при первом раскрытии.
 * Корзинка спрашивает прямо в строке (модалка закрыла бы поповер), удалённая строка схлопывается.
 */
const DocumentDraftList: FC<IDocumentDraftListProps> = ({
    drafts,
    documentTitle,
    onOpenDraft,
    onDeleteDraft,
    activeDraftId,
    emptyText,
    loadDraft = defaultLoadDraft,
    loadOriginTitle = loadDraftOriginTitle,
    initialPreviewId,
    initialConfirmId,
}) => {
    const reduceMotion = useReducedMotion();
    const [openId, setOpenId] = useState<string | null>(initialPreviewId ?? null);
    const [previews, setPreviews] = useState<Record<string, PreviewState>>({});
    // Запрошенные id — в ref, а не в state: два клика в одном кадре не должны дать два запроса.
    const requestedRef = useRef(new Set<string>());
    const [confirmId, setConfirmId] = useState<string | null>(initialConfirmId ?? null);
    const [deleteState, setDeleteState] = useState<DeleteState>(null);

    const ensureLoaded = useCallback(
        (id: string) => {
            if (requestedRef.current.has(id)) return;
            requestedRef.current.add(id);
            setPreviews((prev) => ({ ...prev, [id]: { kind: 'loading' } }));
            loadDraft(id)
                .then((lookup) => {
                    const data = lookup.kind === 'draft' ? lookup.draft.data : null;
                    setPreviews((prev) => ({ ...prev, [id]: { kind: 'ready', preview: summarizeDraftData(data) } }));
                })
                .catch(() => {
                    // ошибка не кэшируется: следующее раскрытие попробует снова
                    requestedRef.current.delete(id);
                    setPreviews((prev) => ({ ...prev, [id]: { kind: 'error' } }));
                });
        },
        [loadDraft],
    );

    const togglePreview = (id: string) => {
        const next = openId === id ? null : id;
        setOpenId(next);
        if (next) ensureLoaded(next);
    };

    // Витрина: раскрытый сразу предпросмотр грузится так же лениво — на первом эффекте.
    useEffect(() => {
        if (initialPreviewId) ensureLoaded(initialPreviewId);
    }, [ensureLoaded, initialPreviewId]);

    // Имена шаблонов и базовых документов: по одному запросу на идентификатор, дальше из кэша
    // модуля. До ответа строка показывает пометку без имени, поэтому список не ждёт сети.
    const [originTitles, setOriginTitles] = useState<Record<string, string | undefined>>({});
    useEffect(() => {
        let alive = true;
        const seen = new Set<string>();
        drafts.forEach((draft) => {
            const origin = draftOriginRef(draft);
            if (!origin) return;
            const key = draftOriginKey(origin);
            if (seen.has(key)) return;
            seen.add(key);
            void loadOriginTitle(origin).then((title) => {
                if (alive)
                    setOriginTitles((prev) => (key in prev && prev[key] === title ? prev : { ...prev, [key]: title }));
            });
        });
        return () => {
            alive = false;
        };
    }, [drafts, loadOriginTitle]);

    const originOf = (draft: IDocumentDraft): string | null => {
        const origin = draftOriginRef(draft);
        return origin ? draftOriginLabel(origin, originTitles[draftOriginKey(origin)]) : null;
    };

    const confirmDelete = async (item: IDocumentDraft) => {
        if (!onDeleteDraft) return;
        setDeleteState({ id: item.id, kind: 'busy' });
        try {
            await onDeleteDraft(item);
            // Строку уберёт владелец из `drafts`; до этого она уже уходит с экрана.
            setDeleteState(null);
            setConfirmId((current) => (current === item.id ? null : current));
        } catch {
            setDeleteState({ id: item.id, kind: 'error' });
        }
    };

    const cancelDelete = () => {
        setConfirmId(null);
        setDeleteState(null);
    };

    const renderPreview = (state: PreviewState | undefined) => {
        if (!state || state.kind === 'loading') {
            return (
                <div className="doc-draft-list__preview-skeleton" aria-busy="true" aria-label="Загрузка предпросмотра">
                    <Skeleton width="55%" height={12} radius={4} />
                    <Skeleton width="80%" height={12} radius={4} />
                    <Skeleton width="40%" height={12} radius={4} />
                </div>
            );
        }
        if (state.kind === 'error') {
            return <div className="doc-draft-list__preview-note">Не удалось загрузить содержимое.</div>;
        }
        if (state.preview.fields.length === 0) {
            return <div className="doc-draft-list__preview-note">Заполненных реквизитов пока нет.</div>;
        }
        return (
            <dl className="doc-draft-list__preview-fields">
                {state.preview.fields.map((field) => (
                    <div key={field.label} className="doc-draft-list__preview-field">
                        <dt>{field.label}</dt>
                        <dd>{field.value}</dd>
                    </div>
                ))}
                {state.preview.more > 0 && (
                    <div className="doc-draft-list__preview-note">
                        ещё {state.preview.more} {plural(state.preview.more, ['реквизит', 'реквизита', 'реквизитов'])}
                    </div>
                )}
            </dl>
        );
    };

    const renderConfirm = (item: IDocumentDraft) => {
        const state = deleteState?.id === item.id ? deleteState.kind : null;
        const busy = state === 'busy';
        const text = busy
            ? 'Удаляем…'
            : state === 'error'
              ? 'Не удалось удалить'
              : `Удалить ${item.name?.trim() ? `«${draftDisplayTitle(item)}»` : 'черновик'}?`;
        return (
            <div
                className="doc-draft-list__confirm"
                role="group"
                aria-label="Удаление черновика"
                data-testid="document-draft-list-confirm"
            >
                <span className="doc-draft-list__confirm-text" title={text}>
                    {text}
                </span>
                <button
                    type="button"
                    className="doc-draft-list__confirm-btn doc-draft-list__confirm-btn--danger"
                    disabled={busy}
                    onClick={() => void confirmDelete(item)}
                    data-testid="document-draft-list-confirm-delete"
                >
                    {state === 'error' ? 'Повторить' : 'Удалить'}
                </button>
                <button
                    type="button"
                    className="doc-draft-list__confirm-btn"
                    disabled={busy}
                    onClick={cancelDelete}
                    data-testid="document-draft-list-confirm-cancel"
                >
                    Отмена
                </button>
            </div>
        );
    };

    const renderRow = (item: IDocumentDraft, expanded: boolean) => {
        const isActive = item.id === activeDraftId;
        const origin = originOf(item);
        return (
            <>
                <button
                    type="button"
                    className="doc-draft-list__row-main"
                    onClick={() => onOpenDraft(item)}
                    data-testid="document-draft-list-row"
                >
                    <span className="doc-draft-list__row-head">
                        <span className="doc-draft-list__row-title">
                            {item.name?.trim() ? draftDisplayTitle(item) : 'Без названия'}
                        </span>
                        {isActive && (
                            <span className="doc-draft-list__chip" data-testid="document-draft-list-active">
                                открыт
                            </span>
                        )}
                        <span className="doc-draft-list__row-when">{formatDraftWhen(item.updatedAt)}</span>
                    </span>
                    {/* Происхождение — своей строкой: в поповере шириной 380 px оно не уместится
                        рядом с именем и временем, а обрезанное «из шаблон…» ничего не значит. */}
                    {origin && (
                        <span className="doc-draft-list__row-origin" data-testid="document-draft-list-origin">
                            {origin}
                        </span>
                    )}
                </button>
                <button
                    type="button"
                    className="doc-draft-list__tool doc-draft-list__peek"
                    aria-label={expanded ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                    title={expanded ? 'Скрыть предпросмотр' : 'Предпросмотр'}
                    aria-expanded={expanded}
                    onClick={() => togglePreview(item.id)}
                    data-testid="document-draft-list-peek"
                >
                    {expanded ? <MdOutlineVisibilityOff size={16} /> : <MdOutlineVisibility size={16} />}
                </button>
                {onDeleteDraft && !isActive && (
                    <button
                        type="button"
                        className="doc-draft-list__tool doc-draft-list__delete"
                        aria-label="Удалить черновик"
                        title="Удалить черновик"
                        onClick={() => {
                            setDeleteState(null);
                            setConfirmId(item.id);
                        }}
                        data-testid="document-draft-list-delete"
                    >
                        <MdDeleteOutline size={16} />
                    </button>
                )}
            </>
        );
    };

    return (
        <div className="doc-draft-list" data-testid="document-draft-list">
            <div className="doc-draft-list__title">Черновики{documentTitle ? ` «${documentTitle}»` : ''}</div>
            {drafts.length === 0 ? (
                <div className="doc-draft-list__empty">{emptyText ?? 'Других черновиков нет'}</div>
            ) : (
                <AnimatePresence initial={false}>
                    {drafts.map((item) => {
                        const expanded = openId === item.id;
                        const confirming = confirmId === item.id;
                        return (
                            <motion.div
                                key={item.id}
                                className={clsx(
                                    'doc-draft-list__item',
                                    expanded && 'doc-draft-list__item--open',
                                    confirming && 'doc-draft-list__item--confirm',
                                )}
                                exit={
                                    reduceMotion
                                        ? { opacity: 0, transition: { duration: 0 } }
                                        : { opacity: 0, height: 0, overflow: 'hidden' }
                                }
                                transition={{
                                    opacity: { duration: 0.16, ease: 'easeOut' },
                                    height: { duration: 0.24, ease: EASE_OUT },
                                }}
                            >
                                <div className="doc-draft-list__row">
                                    {confirming ? renderConfirm(item) : renderRow(item, expanded)}
                                </div>
                                <div className="doc-draft-list__preview" aria-hidden={!expanded}>
                                    <div className="doc-draft-list__preview-inner">
                                        {expanded && (
                                            <div
                                                className="doc-draft-list__preview-body"
                                                data-testid="document-draft-list-preview"
                                            >
                                                {renderPreview(previews[item.id])}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            )}
        </div>
    );
};

export default DocumentDraftList;
